import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetFilteringGroups, useGetActivityGroupAnswerCount, useGetConsolidatedAnswers } from '../../hooks'
import {
  sortAnswersByCompleteDate,
  filterAnswersByActivityGroupIds,
  getUniquePersons,
  getRelevantFilteringGroups,
} from './storyhelpers'
import { moveItemToFront } from '../../helpers/utilities'

export default function useStoryData({ userReady, user, filterGroupItem, familyGroups, activePersonId }) {
  const { data: filteringGroupsData } = useGetFilteringGroups()
  const { activityGroupAnswerCountData } = useGetActivityGroupAnswerCount()
  const { data: consolidatedAnswersData, query: consolidatedAnswersQuery } = useGetConsolidatedAnswers()
  const { t } = useTranslation()

  const allAnswers = useMemo(() => {
    return sortAnswersByCompleteDate(consolidatedAnswersData?.answers)
  }, [consolidatedAnswersData])

  const uniquePersons = useMemo(() => getUniquePersons(familyGroups), [familyGroups])

  const sortedPersonsByUserProfileName = useMemo(() => {
    const sorted = uniquePersons?.sort((a, b) => a.userProfileName.localeCompare(b.userProfileName)) || []
    sorted.unshift({
      memberCisId: 'EVERYONE',
      userProfileName: t('story.everyone', 'Everyone*'),
      activityGroupsIds: [],
    })
    return sorted
  }, [uniquePersons, t])

  const personsWithUserAtFront = useMemo(() => {
    return moveItemToFront(sortedPersonsByUserProfileName, (item) => item.memberCisId === user?.cisId)
  }, [sortedPersonsByUserProfileName, user?.cisId])

  const selectedPerson = useMemo(() => {
    return personsWithUserAtFront?.find((person) => person.memberCisId === activePersonId) || personsWithUserAtFront[0]
  }, [personsWithUserAtFront, activePersonId])

  const filteredAnswers = useMemo(() => {
    return filterAnswersByActivityGroupIds(allAnswers, filterGroupItem, selectedPerson?.memberCisId)
  }, [allAnswers, filterGroupItem, selectedPerson?.memberCisId])

  const filteringGroups = useMemo(() => {
    return getRelevantFilteringGroups({
      rawFilteringGroups: filteringGroupsData?.items,
      allAnswers,
      selectedPersonCisId: selectedPerson?.memberCisId,
    })
  }, [filteringGroupsData?.items, allAnswers, selectedPerson?.memberCisId])

  const selectedFilteringIndex = useMemo(() => {
    return filteringGroups?.findIndex((item) => item?.id === filterGroupItem?.id)
  }, [filteringGroups, filterGroupItem?.id])

  const isAnswersDataReady = useMemo(() => {
    return userReady && !consolidatedAnswersQuery.isLoading
  }, [userReady, consolidatedAnswersQuery.isLoading])

  const showFilterRowNameLabel = useMemo(() => {
    return personsWithUserAtFront?.length > 1
  }, [personsWithUserAtFront])

  return {
    filteringGroupsData,
    isAnswersDataReady,
    allAnswers,
    filteredAnswers,
    filteringGroups,
    selectedFilteringIndex,
    personsWithUserAtFront,
    selectedPerson,
    showFilterRowNameLabel,
    activityGroupAnswerCountData,
  }
}

export const useSetInitialFilterGroup = ({ filteringGroups, filterGroupItem, dispatch }) => {
  useEffect(() => {
    if (filteringGroups?.length > 0 && !filterGroupItem) {
      dispatch({
        actionType: 'UPDATE_SELECTED_FILTER',
        filterGroupItem: filteringGroups[0],
      })
    }
  }, [filteringGroups, filterGroupItem, dispatch])
}

export const useFilterSheetPersonChangeResponse = (infoSheetConfig, activePersonId) => {
  useEffect(() => {
    // Close the filter sheet if the user navigates from the completed activities page/toggle
    if (!activePersonId) {
      infoSheetConfig.close()
    }
  }, [infoSheetConfig, activePersonId])
}
